import { LessonResource } from '../types/v2';

export const lessonResourceSourceType = (resource: LessonResource): string => {
  if (resource.source_mime_type === 'application/vnd.ms-powerpoint'
    || resource.source_mime_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
    return 'powerpoint';
  }
  try {
    const url = new URL(resource.src);
    const host = url.hostname.toLowerCase();
    const path = url.pathname.toLowerCase();
    if (host === 'docs.google.com') {
      if (path.startsWith('/document/')) return 'google_doc';
      if (path.startsWith('/spreadsheets/')) return 'google_sheet';
      if (path.startsWith('/presentation/')) return 'google_slides';
      return 'google_workspace';
    }
    if (host === 'drive.google.com') return 'google_drive';
    if (path.endsWith('.pdf')) return 'pdf';
  } catch {
    // Invalid URLs are rejected by authoring and backend validation; retain a useful fallback label.
  }
  return resource.resource_type || 'link';
};

export const lessonResourceSourceLabel = (sourceType: string): string => ({
  pdf: 'PDF',
  google_doc: 'Google Doc',
  google_sheet: 'Google Sheet',
  google_slides: 'Google Slides',
  powerpoint: 'PowerPoint',
  google_workspace: 'Google Workspace',
  google_drive: 'Google Drive',
  document: 'Document',
  image: 'Image',
  video: 'Video',
  link: 'External link',
  external: 'External link',
}[sourceType] || 'External link');

export const googleDriveResourceType = (mimeType: string): LessonResource['resource_type'] => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'document';
};

export const googleDriveResourceUrl = (fileId: string, mimeType: string, pickerUrl?: string): string => {
  if (pickerUrl?.startsWith('https://')) return pickerUrl;
  if (mimeType === 'application/vnd.google-apps.document') return `https://docs.google.com/document/d/${fileId}/edit`;
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return `https://docs.google.com/spreadsheets/d/${fileId}/edit`;
  if (mimeType === 'application/vnd.google-apps.presentation') return `https://docs.google.com/presentation/d/${fileId}/edit`;
  return `https://drive.google.com/file/d/${fileId}/view`;
};

export const lessonResourcePreviewUrl = (resource: LessonResource): string | null => {
  if (resource.preview_url?.startsWith('https://')) return resource.preview_url;
  const fileId = resource.source_file_id;
  if (!fileId) return resource.resource_type === 'document' ? resource.src : null;
  const mimeType = resource.source_mime_type || '';
  if (mimeType === 'application/vnd.google-apps.document') return `https://docs.google.com/document/d/${fileId}/preview`;
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return `https://docs.google.com/spreadsheets/d/${fileId}/preview`;
  if (mimeType === 'application/vnd.google-apps.presentation') return `https://docs.google.com/presentation/d/${fileId}/preview`;
  return `https://drive.google.com/file/d/${fileId}/preview`;
};

export const googleDriveFileIdFromUrl = (value: string): string | null => {
  try {
    const url = new URL(value);
    if (!['docs.google.com', 'drive.google.com'].includes(url.hostname.toLowerCase())) return null;
    const pathMatch = url.pathname.match(/\/(?:d|folders)\/([A-Za-z0-9_-]+)/);
    const queryId = url.searchParams.get('id');
    const fileId = pathMatch?.[1] || queryId || '';
    return /^[A-Za-z0-9_-]+$/.test(fileId) ? fileId : null;
  } catch {
    return null;
  }
};

export const presentationSlideImageUrl = (
  resource: Pick<LessonResource, 'source_file_id'>,
  slide: { object_id: string; thumbnail_url: string },
): string => {
  if (resource.source_file_id && slide.object_id) {
    return `/api/google/drive/presentation-slide-thumbnail/?file_id=${encodeURIComponent(resource.source_file_id)}&page_object_id=${encodeURIComponent(slide.object_id)}`;
  }
  return slide.thumbnail_url;
};
